import {Component, OnDestroy, OnInit} from '@angular/core';
import {Location} from '@angular/common';
import {ActivatedRoute, Router} from "@angular/router";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbServerErrors} from "../../shared/interceptors/db-server-errors";
import {DbResponse} from "../../shared/interceptors/db-response";
import {NotifierComponent} from "../../shared/components/notifier/notifier.component";
import {Notification} from "../../shared/components/notifier/notification";
import {NotificationType} from "../../shared/components/notifier/notification-type";
import {AuthService} from "../auth.service";
import {CheckoutService} from "../checkout/checkout.service";
import {TypeUtils} from "../../shared/utils/type-utils";
import {GlobalEvent} from "../../shared/services/global-event-dispatcher/global-event";
import {GlobalEventDispatcherService} from "../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {UserSubscription, UserSubscriptionIds, UserSubscriptionType} from "../../user/user-subscription";
import {DbValidators} from "../../shared/validators/DbValidators";
import {CompanyInfo, DbStoreState} from "../../store";
import {filter, first, switchMap, takeUntil} from 'rxjs/operators';
import {Utils} from '../../shared/utils/utils';
import {Config} from "../../config/config";
import {ReCaptchaV3Service} from 'ngx-captcha';
import testimonialsList from "../../../assets/json/testimonials.json";
import steps from "../../components/pages/setup/signup-steps.json";
import {UserService} from "../../user/user.service";
import {Store} from '@ngrx/store';
import * as fromStore from '../../store';
import {Subject} from "rxjs";
import {ResellerCidService} from "../../shared/services/reseller-cid/reseller-cid.service";

@Component({
    selector: 'db-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
    host: { '[class.loading]': 'loading' },
    standalone: false
})
export class SignUpComponent implements OnInit, OnDestroy {
  public loading: boolean;
  public serverErrors: DbServerErrors;
  public hasServerFieldErrors: boolean;
  public Math: any = Math;
  public subscription: UserSubscription;
  public signUpForm: FormGroup;
  public formSubmitted: boolean = false;
  public subscriptionId: number;
  public productId: number;
  public showPassword: boolean = false;
  public websiteId: number;
  public clientId: number;
  public emailChecked: boolean;
  public userDoesNotExist: boolean = false;
  public enableRecaptcha: boolean;
  public client_email: string;
  public firstname: string;
  public lastname: string;
  public userSubscriptionIds: any = UserSubscriptionIds;
  public progress: number = 0;
  public steps: any = steps;
  public region: FormControl;
  public people: any = testimonialsList;
  public userLocalStorage: any = {};

  public showPWDRequirements: boolean = false;
  public isMobile: boolean = false;
  public companyData: string = '';
  public Config = Config;
  public companyInfo: CompanyInfo;
  public googleLoading: boolean = false;
  public _stripe: any;
  public loadingCheckout: boolean;
  public answersData;
  public invitationData: any;
  public invitedTokenInvalid: boolean = false;
  public prefilledAiData: string = '';
  public builderType: string = '';
  public generateWebsite: string = '';
  public remix: string = '';
  public sourceDomainId: string = '';
  public domainId: string = '';
  public siteUrl: string = '';

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
      private _store: Store<DbStoreState>,
    private _location: Location,
    private _router: Router,
    private _route: ActivatedRoute,
    private _authService: AuthService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private _userService: UserService,
    private readonly _checkoutService: CheckoutService,
    private readonly _globalEventDispatcher: GlobalEventDispatcherService,
    private readonly _resellerCidService: ResellerCidService,
  ) {}

  ngOnInit(): void {
    this.companyInfo = this._route.snapshot.data['companyInfo'];
    this._checkFreePlanAvailability();
    this.signUpForm = new FormGroup({
      'firstName': new FormControl('', [
        Validators.required,
        Utils.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, { link: true }, true)
      ]),
      'lastName': new FormControl('', [
        Validators.required,
        Utils.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, { link: true }, true)
      ]),
      'email': new FormControl('', [DbValidators.trim, Validators.required, Validators.email, Validators.pattern(/^[+_\-a-zA-Z0-9]+(\.[+_\-a-zA-Z0-9]+)*@[+_\-a-zA-Z0-9]+(\.[+_\-A-Za-z0-9]+)*(\.[+_\-A-Za-z]{2,24})$/)]),
      'password': new FormControl('',
          [
            Validators.required,
            Validators.minLength(8),
            DbValidators.trim,
            Utils.patternValidator(/\d/, { hasNumber: true }),
            Utils.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            Utils.patternValidator(/[a-z]/, { hasSmallCase: true })
          ]),
      'type': new FormControl('agency_trial')
    });

    // Add TOS consent control if required
    if (this.companyInfo?.branding?.require_tos_consent) {
      this.signUpForm.addControl('terms_privacy_consent_accepted', new FormControl(false));
    }

    const queryParams: any = (<any>this._route.queryParams).value;
    const queryParamsIndex: number = this._router.url.indexOf('?');
      this.invitationData = queryParams.invitation_data || new URL(window.location.href).searchParams.get("invitation_data");
      this.companyData = queryParams.company_info || new URL(window.location.href).searchParams.get("company_info");
      const emailFromWebsite = queryParams.email || new URL(window.location.href).searchParams.get("email");
      this.prefilledAiData = queryParams.prefilledAiData || new URL(window.location.href).searchParams.get("prefilledAiData");
      // Handle both 'builder' and 'builderType' params
      this.builderType = queryParams.builderType || queryParams.builder || new URL(window.location.href).searchParams.get("builderType") || new URL(window.location.href).searchParams.get("builder");
      this.generateWebsite = queryParams['generate-websites'] || new URL(window.location.href).searchParams.get("generate-website");
      this.remix = queryParams['remix'] || new URL(window.location.href).searchParams.get("remix");
      this.sourceDomainId = queryParams['source_domain_id'] || new URL(window.location.href).searchParams.get("source_domain_id");
      this.domainId = queryParams['domain_id'] || new URL(window.location.href).searchParams.get("domain_id");
      this.siteUrl = queryParams['site_url'] || new URL(window.location.href).searchParams.get("site_url");

      console.log('Raw invitation_data:', this.invitationData);
      console.log('Raw company_info:', this.companyData);
      console.log('Email from website:', emailFromWebsite);
      console.log('Prefilled AI Data:', this.prefilledAiData);
      console.log('Builder type:', this.builderType);

      if (emailFromWebsite) {
          this.signUpForm.get('email').setValue(decodeURIComponent(emailFromWebsite));
      }
      if (this.invitationData) {
          this.invitationData = Utils.b64DecodeUnicode(this.invitationData);
          console.log('Decoded invitation_data:', this.invitationData);
          console.log('invitation_key:', this.invitationData.invitation_key);
          console.log('workspace_invitation_id:', this.invitationData.workspace_invitation_id);
          this.signUpForm.get('email').setValue(this.invitationData.client_email);
          this.blurEmail(true);
          localStorage.setItem('subscriptionId', '315');
          // Set payment type based on is_non_billable flag
          if (this.invitationData.is_non_billable) {
            this.signUpForm.get('type').setValue('agency_non_billable');
          }
      }

    this.companyData = queryParams.company_info || new URL(window.location.href).searchParams.get("company_info");
    this.firstname = queryParams.firstname || 'firstname';
    this.lastname = queryParams.lastname || 'lastname';
      this._setAnswersData();
    const utm_source = queryParams.utm_source ? "?utm_source=" + queryParams.utm_source : (queryParams["amp;utm_source"] ? "?utm_source=" + queryParams["amp;utm_source"] : "");
    const utm_medium = queryParams.utm_medium ? "&utm_medium=" + queryParams.utm_medium : (queryParams["amp;utm_medium"] ? (queryParams["amp;utm_source"] ? "&utm_medium=" + queryParams["amp;utm_medium"] : "?utm_medium=" + queryParams["amp;utm_medium"]) : "");

    // Preserve invitation_data in URL if present
    const invitation_param = queryParams.invitation_data ? (utm_source || utm_medium ? "&" : "?") + "invitation_data=" + queryParams.invitation_data : "";

    if (queryParamsIndex !== -1) {
      this._location.replaceState(this._router.url.substring(0, queryParamsIndex) + utm_source + utm_medium + invitation_param);
    }
  }


    private _setAnswersData(): void {
        this.answersData = {};
        this.answersData.Role = {
            "answer": "SMB",
            "id": "eLLnqwOWEyzs"
        };
        this.answersData.WebsiteNumber = {
            "answer": "1-3",
            "id": "EQ6sqoPuMvGg"
        };
        if (this.productId === Config.aiCreateWebsitePluginId) {
            this.answersData.LastChosenOption = {
                "answer": "ai-convert",
                "id": ""
            };
        }
    }

    private updateSignUpForData(): void {
        // this._store.dispatch(new fromStore.LoadUser());
        this._authService.updateSignUpFor(this.answersData, true)
            .pipe(
                first(response => !!response, null),
            )
            .subscribe(() => {
                this.updateUserLocalStorage();
            });
    }

    private updateUserLocalStorage(): void {
        this.userLocalStorage['signUpFor'] = this.answersData;
        this._userService.setToLocal(this.userLocalStorage)
            .pipe(
                first(response => !!response, null),
            )
            .subscribe((response) => {
                if (response.success) {
                    this._store.dispatch(new fromStore.LoadUserLocalStorage());
                    this._store.dispatch(new fromStore.LoadUser());
          /*          if (this.productId === Config.aiCreateWebsitePluginId) {
                        localStorage.setItem('dbFromSetup', '1');
                        setTimeout(() => {
                            this._router.navigate(['/setup/ai-convert'], {queryParams: {navigate_to: this.navigateTo}});
                        }, 1000);
                    }*/
          /*          if (this.productId === Config.managerPluginId) {
                        this._router.navigate(['/setup/change-hosting'], {queryParams: {navigate_to: this.navigateTo}});
                    }

                    else {
                        this._router.navigate(['/setup'], {queryParams: {navigate_to: this.navigateTo, company_info: this.companyData}});
                    }*/
                }
            });
    }

  public signUpLogin(loginData, recaptchaToken): void {
    this._authService.login(loginData, false, recaptchaToken)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response) => {
        if (response.success) {
          // Check if we have prefilledAiData to pass along
          const queryParamsForRedirect: any = {};
          if (this.prefilledAiData) {
            queryParamsForRedirect.prefilledAiData = this.prefilledAiData;
            if (this.builderType) {
              // Use 'builderType' param name expected by WebsitePromptComponent
              queryParamsForRedirect.builderType = this.builderType;
            }
            const prefilledData = JSON.parse(this.prefilledAiData);

            if (prefilledData.images) {
                  queryParamsForRedirect.images = prefilledData.images;
            }

            if (this.generateWebsite) {
              // Use 'generateWebsite' param name expected by WebsitePromptComponent
              queryParamsForRedirect['generate-website'] = this.generateWebsite;
            }
          }

          if (this.remix && this.sourceDomainId) {
            queryParamsForRedirect['remix'] = this.remix;
            queryParamsForRedirect['source_domain_id'] = this.sourceDomainId;
            queryParamsForRedirect['generate-website'] = '1';
            if (this.domainId) {
              queryParamsForRedirect['domain_id'] = this.domainId;
            }
            if (this.siteUrl) {
              queryParamsForRedirect['site_url'] = this.siteUrl;
            }
          }

          // Navigate to /websites with query params if they exist
          this._router.navigate(['/websites'], {
            queryParams: Object.keys(queryParamsForRedirect).length > 0 ? queryParamsForRedirect : undefined
          });

          if (this.subscription && this.subscription?.trialClient) {
              this.updateSignUpForData();
          }
        }
        else {
          this._router.navigate(['/login'], { queryParams: { userNotVerified: true }});
        }
        return null;
      });
  }


  public blurEmail(direct = false): void {
    if (this.signUpForm.controls['email'].valid && (this.signUpForm.controls['email'].touched || direct)) {
      this._authService.checkUserExists(this.signUpForm.value.email)
        .pipe(filter((response: DbResponse): boolean => !!response),
        first())
        .subscribe((response) => {
          this.emailChecked = true;
          this.userDoesNotExist = !!response.success;
          this.serverErrors = response.serverErrors;
          if (response.success) { this.serverErrors = null; }
        });
    }
  }

  public checkoutRequest() {
    if (this.invitationData && this.invitationData.workspace_invitation_id) {
      localStorage.setItem('dbFromInvitation', '1');
    }
    this.loadingCheckout = true;

    // Fired on the click itself, before the request, so failed attempts
    // (validation, email taken, declined card) stay in the denominator.
    // The reseller cid is the only identifier available here — the account
    // does not exist yet, so there is no twdb_user_id to send. It is what
    // lets the reseller join this click to their own marketing-site session.
    if (typeof window['gtag'] === 'function') {
      const resellerCid = this._resellerCidService.get();
      window['gtag']("event", "signup_button_click", resellerCid ? { cid: resellerCid } : undefined);
    }

    this._authService.checkout(
      this.signUpForm.getRawValue(),
      this.invitationData?.workspace_invitation_id || null,
      this.invitationData?.invitation_key || null
    )
      .pipe(filter((response: DbResponse): boolean => !!response),
          first())
      .subscribe((response) => {
        if (response.success) {
          this.loginAfterSignUp();
        }
        else if ( !response.success && this.invitationData && this.invitationData.workspace_invitation_id ) {
            this.invitedTokenInvalid = true;
            NotifierComponent.notify(new Notification(response.serverErrors.message, NotificationType.DANGER, true));
            this.loadingCheckout = false;
        }
        else if (response.serverErrors && TypeUtils.isEmpty(response.serverErrors.fields)) {
          NotifierComponent.notify(new Notification(response.serverErrors.message, NotificationType.DANGER, true));
          this.loadingCheckout = false;
        }
        else {
          this.serverErrors = response.serverErrors;
          this.checkHasServerFieldErrors(response.serverErrors);
          this.loadingCheckout = false;
        }
      });
  }



  private checkHasServerFieldErrors(serverErrors: DbServerErrors): void {
    const FIELDS_TO_CHECK = [
      'name',
      'first_name',
      'last_name',
      'email',
      'password',
      'stripe_token',
      'terms_privacy_consent_accepted',
    ] as const;

    type CheckedField = typeof FIELDS_TO_CHECK[number];

    const fields = serverErrors?.fields as Partial<Record<CheckedField, string>> | undefined;

    const errorMessages: string[] = [];
    if (fields) {
      for (const f of FIELDS_TO_CHECK) {
        const msg = fields[f];
        if (msg) errorMessages.push(msg);
      }
    }

    this.hasServerFieldErrors = errorMessages.length > 0;

    if (this.hasServerFieldErrors) {
      NotifierComponent.notify(
        new Notification(errorMessages.join('. '), NotificationType.DANGER, true),
      );
    }
  }

  public loginAfterSignUp(): void {

    const loginData = {
      'email': this.signUpForm.value.email,
      'password': this.signUpForm.value.password,
    };

    if (this.enableRecaptcha) {
      let recaptchaToken;
      this.reCaptchaV3Service.execute(Config.googleRecaptcha, 'signup', (token) => {
        recaptchaToken = token;
        this.signUpLogin(loginData, recaptchaToken);
      });
    }
    else {
      this.signUpLogin(loginData, null);
    }

  }


  public progressPercent(): void {
    this.progress = Utils.progressPercent(this.signUpForm.controls['password']);
  }

  public verifyToken(googleToken: string): void {
    if (!googleToken) return;
    this.googleLoading = true;
    this._checkoutService.verifyGoogleToken(googleToken, null, null, null, null, null, null, null)
      .pipe(filter((response: DbResponse): boolean => !!response), first())
      .subscribe((response) => {
        if (response.success) {
          if (response.data && response.data.email && response.data.auth_key) {
            this._router.navigate(['/login'], { queryParams: { auth_key: response.data.auth_key, email: response.data.email } });
            this.googleLoading = false;
          } else {
            this._globalEventDispatcher.dispatchEvent(GlobalEvent.userLogIn);
            this._store.dispatch(new fromStore.UserLogIn);
            const queryParamsForRedirect: any = { fromGoogleSignIn: true, fromSignup: 1 };
            if (this.prefilledAiData) {
              queryParamsForRedirect.prefilledAiData = this.prefilledAiData;
              if (this.builderType) {
                queryParamsForRedirect.builderType = this.builderType;
              }
              const prefilledData = JSON.parse(this.prefilledAiData);
              if (prefilledData.images) {
                queryParamsForRedirect.images = prefilledData.images;
              }
              if (this.generateWebsite) {
                queryParamsForRedirect['generate-website'] = this.generateWebsite;
              }
            }
            if (this.remix && this.sourceDomainId) {
              queryParamsForRedirect['remix'] = this.remix;
              queryParamsForRedirect['source_domain_id'] = this.sourceDomainId;
              queryParamsForRedirect['generate-website'] = '1';
              if (this.domainId) {
                queryParamsForRedirect['domain_id'] = this.domainId;
              }
              if (this.siteUrl) {
                queryParamsForRedirect['site_url'] = this.siteUrl;
              }
            }
            this._router.navigate(['/websites'], { queryParams: queryParamsForRedirect });
            this.googleLoading = false;
          }
        } else {
          if (response.serverErrors && response.serverErrors.message !== 'Client is not verified') {
            this.serverErrors = response.serverErrors;
            this.checkHasServerFieldErrors(response.serverErrors);
          }
          this.googleLoading = false;
        }
      });
  }

  private _checkFreePlanAvailability(): void {
      this._store.select(fromStore.getUserAvailableSubscriptionsLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserAvailableHostingSubscriptions())),
        first(res => !!res.length),
      )
      .subscribe((availableSubscriptions: UserSubscription[]) => {
        console.log('Available subscriptions:', availableSubscriptions);
        // Access control is now handled by PaymentSubscriptionGuard
        // No need to check for free plans here
      });
  }

  ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

}
